// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function AiCalorieCounterTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Smart Calorie – AI Food Tracker"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “Smart
        Calorie – AI Food Tracker” (the “App”). By downloading, installing,
        accessing, or using the App, you agree to be bound by these Terms. If
        you do not agree, you must uninstall the App and discontinue all use.
        Where required by platform rules or local law, additional terms may
        apply; in case of conflict with App Store or Google Play terms, the
        platform terms may prevail to the extent required.
      </p>

      <div className={s.title}>
        2) Description of the App (Non-Medical Nutrition Tool)
      </div>
      <p>
        The App is a calorie and macronutrient tracking tool that helps you log
        meals, estimate daily energy intake, and view progress toward a daily
        calorie goal. Onboarding may collect information such as your age,
        height, weight, activity level, and goal (for example, weight loss,
        maintenance, or gain) to calculate an approximate daily calorie target
        using standard formulas (such as Mifflin–St Jeor). You can search for
        foods via third-party nutrition databases, add items to your daily log,
        and review daily totals for calories and macronutrients.
      </p>
      <p>
        The App is intended for general wellness and self-tracking only. It is
        not a medical device and does not provide medical, nutritional, fitness,
        or mental health advice. It must not be used to diagnose, treat, cure,
        or prevent any disease or eating disorder. Always consult qualified
        health professionals before making significant changes to your diet,
        activity, or weight goals.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent. If you have a
        medical condition (including but not limited to diabetes, eating
        disorders, cardiovascular disease, or pregnancy), you should use the App
        only under the guidance of a qualified healthcare professional.
      </p>

      <div className={s.title}>4) Onboarding Data and Daily Targets</div>
      <p>
        During onboarding, the App may ask for information such as goal, sex,
        age, height, weight, and activity level to estimate daily calorie needs
        and allocate macronutrient ranges. These values are based on population
        formulas and assumptions and may not reflect your individual medical
        needs. They are approximations for self-tracking, not prescriptions.
      </p>
      <p>
        You are responsible for:
      </p>
      <ul>
        <li>providing accurate and up-to-date onboarding information;</li>
        <li>
          periodically reviewing and updating your details (for example, when
          weight or activity changes);
        </li>
        <li>
          treating daily targets as informational, not as strict limits or
          medical advice.
        </li>
      </ul>
      <p>
        Do not engage in extreme dieting, rapid weight loss attempts, or any
        unsafe behavior based solely on App estimates.
      </p>

      <div className={s.title}>
        5) Food Database, Third-Party APIs, and Accuracy
      </div>
      <p>
        The App may use third-party APIs and databases (for example, Nutritionix
        or similar providers) to retrieve nutritional information for foods you
        search and log. These databases are maintained by third parties and are
        subject to their own terms, privacy policies, and limitations. Calorie
        and macronutrient values are estimates and may differ from labels,
        recipes, or actual meals consumed.
      </p>
      <p>
        Food data may vary due to brand differences, preparation methods,
        portion sizes, and database updates. You are responsible for verifying
        information against reliable sources (such as package labels or
        guidance from health professionals) before relying on it. We do not
        guarantee that any nutritional information shown in the App is complete,
        correct, or suitable for your specific needs.
      </p>

      <div className={s.title}>6) Logging, History, and Free Tier Limits</div>
      <p>
        The App may allow you to log meals, view a daily dashboard, and see
        calorie and macro totals for each day. In the free version, use may be
        limited—for example, to a set number of active tracking days, restricted
        access to historical data, or limited reporting features. After a
        threshold of usage (for example, several days of logging), the App may
        display a paywall and restrict further access to some features unless
        you subscribe.
      </p>
      <p>
        Logged data is generally stored locally on your device (for example,
        using Hive or similar storage) and may be backed up or synced depending
        on your device and system settings. Deleting the App or clearing its
        data may permanently remove your logs and history.
      </p>

      <div className={s.title}>7) Subscriptions, Trials, and Billing</div>
      <p>
        Certain features—such as unlimited day tracking, extended history,
        additional reports, or other premium tools—may be available only through
        paid auto-renewable subscriptions or other in-app purchases
        (“Subscriptions”), such as a weekly plan with a free trial period.
        Exact pricing, trial length, and benefits may vary by region and may be
        updated from time to time as shown in the App and app stores.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are governed by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>8) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features, such as unlimited tracking days or access to previous days.
        An internet connection may be required to validate or refresh
        entitlements after long offline periods. If platform billing reverses,
        refunds, or flags a transaction as fraudulent, related entitlements may
        be limited, suspended, or revoked.
      </p>

      <div className={s.title}>
        9) Health, Safety, and Non-Medical Disclaimer
      </div>
      <p>
        The App does not provide personalized medical, nutritional, or fitness
        advice. Any daily targets, charts, or messages you see in the App are
        generated by general algorithms and are not a substitute for guidance
        from qualified healthcare professionals. Calorie and macro tracking may
        not be appropriate for everyone, especially people with a history of
        eating disorders, certain medical conditions, or those who are pregnant
        or under age thresholds defined by local law.
      </p>
      <p>
        You should always:
      </p>
      <ul>
        <li>
          seek advice from doctors, registered dietitians, or other licensed
          professionals before making substantial changes to your diet or
          activity;
        </li>
        <li>
          avoid using the App to pursue extreme calorie restriction or unsafe
          weight goals;
        </li>
        <li>
          stop using the App and seek help if tracking causes distress or
          unhealthy behavior.
        </li>
      </ul>

      <div className={s.title}>10) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful, responsible, and personal
        self-tracking purposes and not to:
      </p>
      <ul>
        <li>
          use the App in ways that violate applicable health, privacy, or
          consumer protection laws;
        </li>
        <li>
          share or export someone else’s personal health or dietary information
          without consent;
        </li>
        <li>
          attempt to reverse engineer, decompile, or extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, usage limits, or paywalls separating
          free and premium features;
        </li>
        <li>
          use automated tools or scripts to overload, misuse, or scrape the App
          or its backend services.
        </li>
      </ul>

      <div className={s.title}>11) Intellectual Property</div>
      <p>
        You retain ownership of your logged data (for example, meals you add and
        notes you create), subject to rights of third parties. We may process
        such data internally to operate, maintain, and improve the App (for
        example, to calculate daily totals, show history, and generate basic
        analytics).
      </p>
      <p>
        All software, user interface designs, charts, icons, logos, and other
        materials provided with the App (excluding your data and third-party
        content) are owned by the publisher or its licensors and are protected
        by intellectual property laws. Subject to these Terms and any
        Subscription limitations, you are granted a limited, non-exclusive,
        non-transferable, revocable license to use the App and its features for
        personal wellness tracking.
      </p>

      <div className={s.title}>12) Analytics and Crash Reporting</div>
      <p>
        We may use analytics and crash-reporting tools (for example, Firebase
        Analytics and Crashlytics or similar services) to understand how the App
        is used, monitor performance, and improve stability. Typical events may
        include app launches, onboarding completion, food additions, paywall
        views, and subscription outcomes. Aggregated or de-identified data may
        be used to guide product decisions. Where required by law, we will
        obtain necessary permissions or provide options to manage analytics
        preferences.
      </p>

      <div className={s.title}>13) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        We do not warrant that:
      </p>
      <ul>
        <li>calorie or macronutrient data will be accurate or complete;</li>
        <li>
          calculated daily targets will be appropriate for your individual
          health needs;
        </li>
        <li>
          the App will always be available, error-free, or compatible with every
          device or OS version.
        </li>
      </ul>

      <div className={s.title}>14) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data (including loss of logged meals or history), or business
        interruption arising from or related to your use of or inability to use
        the App or your reliance on any estimates or information presented in
        the App. Our total aggregate liability to you for direct damages arising
        out of or relating to the App will be limited to the amount you paid for
        Subscriptions during the twelve (12) months prior to the claim, or the
        minimum amount permitted by applicable law if greater. Some
        jurisdictions do not allow certain limitations or exclusions, so parts
        of this section may not apply to you.
      </p>

      <div className={s.title}>15) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your dietary decisions based on App
        data, your breach of these Terms, or your violation of any rights of a
        third party.
      </p>

      <div className={s.title}>16) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>17) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Smart Calorie – AI Food Tracker, which explains how information is
        collected, used, stored, and shared, including onboarding data,
        nutrition logs, technical data, and analytics. In case of conflict
        between these Terms and the Privacy Policy regarding data processing,
        the Privacy Policy will guide how we handle personal data, while these
        Terms govern your use of the App and its features. You should review the
        Privacy Policy regularly to understand current practices.
      </p>

      <div className={s.title}>18) Changes to the App and to These Terms</div>
      <p>
        We may update the App, add or remove features (including changes to free
        and premium offerings), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or via other reasonable means. By continuing to use the App after
        changes take effect, you accept the updated Terms. If you do not agree
        to the revised Terms, you must stop using the App and uninstall it.
      </p>

      <div className={s.title}>19) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account-specific
        or purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
